import { useState } from "react";
import "../components/login/Login.css";

import StatusDisplay from "../components/feedback/Status";
import LoginForm from "../components/login/LoginForm";
import { Status } from "../enums/status";
import useRequests from "../hooks/http";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function Login() {
  const { post } = useRequests();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [appStatus, setStatus] = useState(Status.PENDING);
  const [message, setMessage] = useState("");

  const login = async (e: LoginProps) => {
    if (!isOAuthContext()) {
      return directPasswordLogin(e);
    }

    const clientId = params.get("client_id") as string;
    let redirect = params.get("redirect_uri") as string;

    setStatus(Status.LOADING);

    const initiateResult = await post(
      "/initiate",
      intitiateFormData(clientId, redirect),
      undefined,
    );

    if (!initiateResult.data) {
      setStatus(Status.ERROR);
      setMessage("Failed to initiate authorization flow");
      return;
    }

    const { state } = initiateResult.data;

    const { data, status } = await post("/oauth/password", null, {
      email: e.username,
      password: e.password,
      state,
    });

    if (!status || status >= 400) {
      setStatus(Status.ERROR);
      setMessage(data?.detail || data?.message || "Login failed");
      return;
    }

    const authCode = data.auth_code;
    if (!authCode) {
      setStatus(Status.ERROR);
      setMessage("No authorization code received");
      return;
    }

    setStatus(Status.SUCCESS);
    setMessage("Successfully authorized! Redirecting...");

    redirect = `${redirect}?code=${authCode}`;
    window.location.href = redirect;
  };

  async function directPasswordLogin(e: LoginProps) {
    setStatus(Status.LOADING);
    const { data, status } = await post("/platform/password", e);
    if (status === 200) {
      if (data.status === "tenant_selection_required") {
        sessionStorage.setItem("janus_pending_token", data.token);
        sessionStorage.setItem(
          "janus_pending_tenants",
          JSON.stringify(data.tenants),
        );
        return navigate("/select-tenant");
      }
      setStatus(Status.SUCCESS);
      setMessage(data.message);

      await refresh();
      return navigate("/app");
    }
    setStatus(Status.ERROR);
    setMessage(data?.message || data?.detail || "Login failed");
  }

  function isOAuthContext() {
    return params.get("client_id") && params.get("redirect_uri");
  }

  function intitiateFormData(
    clientId: string,
    redirectUri: string,
  ): URLSearchParams {
    const form = new URLSearchParams();
    form.append("client_id", clientId);
    form.append("redirect_uri", redirectUri);
    form.append("grant_type", "authorization_code");
    return form;
  }

  function initiateOauthAuthCodeFlow(
    code: string,
    redirectUri: string,
    state: string,
    clientId: string,
  ) {
    const form = new URLSearchParams();
    form.append("client_id", clientId);
    form.append("redirect_uri", redirectUri);
    form.append("code", code);
    form.append("state", state);
    form.append("grant_type", "authorization_code");
    return form;
  }

  async function directConsoleLogin(e: any) {
    const path = "/platform/oauth/google";
    const { data, status } = await post(path, null, { code: e.code });
    if (status === 200 && data.status === "tenant_selection_required") {
      sessionStorage.setItem("janus_pending_token", data.token);
      sessionStorage.setItem(
        "janus_pending_tenants",
        JSON.stringify(data.tenants),
      );
      return navigate("/select-tenant");
    }
    await refresh();
    return navigate("/app");
  }

  const google = async (e: any) => {
    if (!isOAuthContext()) {
      return directConsoleLogin(e);
    }
    const clientId = params.get("client_id") as string;
    let redirect = params.get("redirect_uri") as string;

    const initiateResult = await post(
      "/initiate",
      intitiateFormData(clientId, redirect),
      undefined,
    );

    if (!initiateResult.data) {
      setStatus(Status.ERROR);
      setMessage("Failed to initiate authorization flow");
      return;
    }

    const { state } = initiateResult.data;
    setStatus(Status.LOADING);

    let path = "/oauth/google";

    const { data, status } = await post(
      path,
      initiateOauthAuthCodeFlow(e.code, redirect, state, clientId),
    );
    const token = data.access_token;

    if (!status) {
      setMessage(
        "Shoot, having some issues with the authorization server. Seems we have some gremlins chewing cables here, chief!",
      );
      return setStatus(Status.ERROR);
    }

    if (status === 401) {
      setMessage(data.detail.message);
      return setStatus(Status.ERROR);
    }

    setStatus(Status.SUCCESS);
    setMessage(
      data.message ||
        "Successfully authorized! Proceeding with redirect or login flow now.",
    );

    redirect = `${redirect}?code=${token}`;
    window.location.href = redirect as string;
    return;
  };

  return (
    <LoginForm callback={login} google={google}>
      <StatusDisplay status={appStatus} message={message} />
    </LoginForm>
  );
}
